import React, { useEffect, useState } from 'react';
import { Save, Check, X } from 'lucide-react';
interface TaxDeduction {
  id: string;
  name: string;
  requirements: string;
  benefit: string | number;
  category: string;
  deadline: string;
  accomplished: boolean;
}
export function TaxMax() {
  const [deductions, setDeductions] = useState<TaxDeduction[]>([{
    id: '1',
    name: 'Standard Deduction',
    requirements: 'None',
    benefit: 24800,
    category: 'ATL Tax Reduction',
    deadline: 'Dec 31',
    accomplished: true
  }, {
    id: '2',
    name: '401(k)',
    requirements: 'None',
    benefit: '(up to) $19500',
    category: 'ATL AGI Reduction',
    deadline: 'Dec 31',
    accomplished: true
  }, {
    id: '3',
    name: 'IRA',
    requirements: 'None',
    benefit: 12000,
    category: 'BTL AGI Reduction',
    deadline: 'Before filing',
    accomplished: false
  }, {
    id: '4',
    name: 'Child Care',
    requirements: '$10,000 expenses',
    benefit: 3000,
    category: 'NR Tax Credit',
    deadline: 'Dec 31',
    accomplished: false
  }, {
    id: '5',
    name: 'Student Loan Interest',
    requirements: '$2,500 paid',
    benefit: 2500,
    category: 'AGI Reduction',
    deadline: 'Dec 31',
    accomplished: false
  }, {
    id: '6',
    name: 'HSA',
    requirements: 'HSA',
    benefit: 7100,
    category: 'ATL AGI Reduction',
    deadline: 'Dec 31',
    accomplished: true
  }, {
    id: '7',
    name: 'Home Office',
    requirements: 'Home office',
    benefit: 750,
    category: 'BTL AGI Reduction',
    deadline: 'Dec 31',
    accomplished: true
  }, {
    id: '8',
    name: 'Savers',
    requirements: '< $38,500 AGI',
    benefit: 2000,
    category: 'R Tax Credit',
    deadline: 'Dec 31',
    accomplished: false
  }, {
    id: '9',
    name: 'Child',
    requirements: '< $400,000 AGI',
    benefit: 10800,
    category: 'R Tax Credit',
    deadline: 'Dec 31',
    accomplished: true
  }, {
    id: '10',
    name: 'Earned Income',
    requirements: '< $56,844 AGI',
    benefit: 6660,
    category: 'R Tax Credit',
    deadline: 'Dec 31',
    accomplished: true
  }, {
    id: '11',
    name: 'Already paid taxes',
    requirements: '$0 Tax liability',
    benefit: 6298.55,
    category: 'R Tax Credit',
    deadline: 'Dec 31',
    accomplished: false
  }, {
    id: '12',
    name: 'AZ Welfare',
    requirements: '30864',
    benefit: 2964,
    category: 'State benefit',
    deadline: 'Dec 31',
    accomplished: false
  }, {
    id: '13',
    name: 'AHCSS for all',
    requirements: '42338.4',
    benefit: 'Charity gets AHCSS',
    category: 'State benefit',
    deadline: 'Dec 31',
    accomplished: false
  }, {
    id: '14',
    name: 'AHCSS for kids',
    requirements: '62894',
    benefit: 'Kids get AHCSS',
    category: 'State benefit',
    deadline: 'Dec 31',
    accomplished: true
  }]);
  const [earnings, setEarnings] = useState(65000);
  const [atlTaxes, setAtlTaxes] = useState(12000);
  const [btlTaxes, setBtlTaxes] = useState(11250);
  const [estimatedTax, setEstimatedTax] = useState(1000);
  const [refundableCredits, setRefundableCredits] = useState(21357);
  const [taxRefund, setTaxRefund] = useState(20357);
  const [saveMessage, setSaveMessage] = useState('');
  useEffect(() => {
    // Load data from localStorage on initial render
    const savedData = localStorage.getItem('taxMaxData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setDeductions(parsedData.deductions || deductions);
        setEarnings(parsedData.earnings || earnings);
        setAtlTaxes(parsedData.atlTaxes || atlTaxes);
        setBtlTaxes(parsedData.btlTaxes || btlTaxes);
        setEstimatedTax(parsedData.estimatedTax || estimatedTax);
        setRefundableCredits(parsedData.refundableCredits || refundableCredits);
        setTaxRefund(parsedData.taxRefund || taxRefund);
      } catch (error) {
        console.error('Error loading saved tax data:', error);
      }
    }
  }, []);
  const toggleAccomplished = (id: string) => {
    setDeductions(deductions.map(deduction => deduction.id === id ? {
      ...deduction,
      accomplished: !deduction.accomplished
    } : deduction));
  };
  const handleSave = () => {
    const dataToSave = {
      deductions,
      earnings,
      atlTaxes,
      btlTaxes,
      estimatedTax,
      refundableCredits,
      taxRefund,
      lastSaved: new Date().toISOString()
    };
    localStorage.setItem('taxMaxData', JSON.stringify(dataToSave));
    // Show save confirmation message
    setSaveMessage('Tax data saved successfully!');
    // Clear the message after 3 seconds
    setTimeout(() => {
      setSaveMessage('');
    }, 3000);
  };
  return <div className="p-8 max-w-6xl mx-auto" data-id="element-240">
      <div className="flex items-center justify-between mb-6" data-id="element-241">
        <h1 className="text-3xl font-bold text-gray-800" data-id="element-242">TaxMax</h1>
        <div className="flex items-center" data-id="element-243">
          {saveMessage && <span className="text-green-600 mr-3 font-medium" data-id="element-244">
              {saveMessage}
            </span>}
          <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors" data-id="element-245">
            <Save size={18} data-id="element-246" />
            Save
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-6" data-id="element-247">
        <div className="overflow-x-auto" data-id="element-248">
          <table className="w-full" data-id="element-249">
            <thead data-id="element-250">
              <tr className="bg-gray-50 text-left" data-id="element-251">
                <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-252">Name</th>
                <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-253">
                  Requirements
                </th>
                <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-254">Benefit</th>
                <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-255">
                  Category
                </th>
                <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-256">
                  Deadline
                </th>
                <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-257">
                  Accomplished?
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-258">
              {deductions.map(deduction => <tr key={deduction.id} className="hover:bg-gray-50" data-id="element-259">
                  <td className="px-6 py-4 font-medium text-gray-800" data-id="element-260">
                    {deduction.name}
                  </td>
                  <td className="px-6 py-4 text-gray-600" data-id="element-261">
                    {deduction.requirements}
                  </td>
                  <td className="px-6 py-4 text-gray-600" data-id="element-262">
                    {typeof deduction.benefit === 'number' ? deduction.benefit.toLocaleString() : deduction.benefit}
                  </td>
                  <td className="px-6 py-4 text-gray-600" data-id="element-263">
                    {deduction.category}
                  </td>
                  <td className="px-6 py-4 text-gray-600" data-id="element-264">
                    {deduction.deadline}
                  </td>
                  <td className="px-6 py-4" data-id="element-265">
                    <button onClick={() => toggleAccomplished(deduction.id)} className={`p-1 rounded-full ${deduction.accomplished ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`} data-id="element-266">
                      {deduction.accomplished ? <Check size={18} data-id="element-267" /> : <X size={18} data-id="element-268" />}
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-269">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-270">
          <div data-id="element-271">
            <p className="text-sm text-gray-500 mb-1" data-id="element-272">Earnings</p>
            <p className="text-lg font-bold text-gray-800" data-id="element-273">
              ${earnings.toLocaleString()}
            </p>
          </div>
          <div data-id="element-274">
            <p className="text-sm text-gray-500 mb-1" data-id="element-275">ATL Taxes</p>
            <p className="text-lg font-bold text-gray-800" data-id="element-276">
              ${atlTaxes.toLocaleString()}
            </p>
          </div>
          <div data-id="element-277">
            <p className="text-sm text-gray-500 mb-1" data-id="element-278">BTL Taxes</p>
            <p className="text-lg font-bold text-gray-800" data-id="element-279">
              ${btlTaxes.toLocaleString()}
            </p>
          </div>
          <div data-id="element-280">
            <p className="text-sm text-gray-500 mb-1" data-id="element-281">Estimated Tax</p>
            <p className="text-lg font-bold text-gray-800" data-id="element-282">
              ${estimatedTax.toLocaleString()}
            </p>
          </div>
          <div data-id="element-283">
            <p className="text-sm text-gray-500 mb-1" data-id="element-284">Refundable Credits</p>
            <p className="text-lg font-bold text-green-600" data-id="element-285">
              ${refundableCredits.toLocaleString()}
            </p>
          </div>
          <div data-id="element-286">
            <p className="text-sm text-gray-500 mb-1" data-id="element-287">Tax Refund</p>
            <p className="text-lg font-bold text-green-600" data-id="element-288">
              ${taxRefund.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>;
}